import React, { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import axios from 'axios';
import { ajaxconfig } from "../ajaxconfig";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal } from 'react-bootstrap';
import MyTextEditor from '../admin/MyTextEditor';
import { 
  PublicMassCbtQuestionGet,
  VerifyPublicGetCbtPassCode, 
  StudentTheorySubmit, 
  StudentClearAnswer, 
  StudentAnswerSubmit,  
  FinalizedGrade 
} from '../serverurl';

const CbtQuestion = () => {
  // State variables
  const [db_cbt_question, set_db_cbt_question] = useState([]);
  const [cbt_data, set_cbt_data] = useState([]);
  const [submittedGrade, setsubmittedGrade] = useState(false);
  const [open, setOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  
  // URL parameters
  const url_param = new URLSearchParams(window.location.search);
  const cbt_pass_code = url_param.get("cbt_pass_code");
  const cbt_permit_code = url_param.get("associated_user");
  const [cbt_test_id, set_cbt_test_id] = useState(null);
  const navigate = useNavigate();

  // Timer state
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  const config = ajaxconfig();

  // Fetch data - moved inside initializeExam to prevent re-creation on re-renders
  const GetQuestion = PublicMassCbtQuestionGet + cbt_pass_code + "/" + cbt_permit_code + "/" + cbt_permit_code;
  
  // Enhanced keyboard navigation
  const handleKeyPress = useCallback((event) => {
    // Prevent default for all navigation keys
    if (event.ctrlKey || ['ArrowLeft', 'ArrowRight', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(event.key)) {
      event.preventDefault();
    }

    // Ctrl + Number navigation (1-9, 0 for 10)
    if (event.ctrlKey && !isNaN(event.key)) {
      const number = parseInt(event.key);
      let targetIndex;
      
      if (number === 0) {
        targetIndex = 9; // Ctrl+0 goes to question 10
      } else {
        targetIndex = number - 1; // Ctrl+1 goes to question 1 (index 0)
      }
      
      // Validate the target index
      if (targetIndex >= 0 && targetIndex < db_cbt_question.length) {
        setCurrentQuestionIndex(targetIndex);
        toast.info(`Navigated to question ${targetIndex + 1}`);
      }
      return;
    }
    
    // Arrow key navigation
    if (!event.ctrlKey) {
      switch(event.key) {
        case 'ArrowLeft':
          if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
          }
          break;
        case 'ArrowRight':
          if (currentQuestionIndex < db_cbt_question.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
          }
          break;
        default:
          break;
      }
    }
  }, [db_cbt_question.length, currentQuestionIndex]);

  // Prevent page refresh/exit
  const handleBeforeUnload = useCallback((event) => {
    if (examStarted && !submittedGrade) {
      event.preventDefault();
      event.returnValue = 'Your exam progress will be lost if you leave this page. Are you sure?';
    }
  }, [examStarted, submittedGrade]);

  // Handle image clicks in rich text content
  const handleImageClick = useCallback((imgUrl) => {
    setExpandedImage(imgUrl);
  }, []);

  // Set up image click listeners for rich text content
  useEffect(() => {
    const handleRichTextImageClick = (event) => {
      if (event.target.tagName === 'IMG' && event.target.closest('.prose')) {
        event.preventDefault();
        event.stopPropagation();
        handleImageClick(event.target.src);
      }
    };

    document.addEventListener('click', handleRichTextImageClick);
    
    return () => {
      document.removeEventListener('click', handleRichTextImageClick);
    };
  }, [handleImageClick]);

  useEffect(() => {
    initializeExam();
    
    // Add event listeners
    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(timerRef.current);
    };
  }, []); // Empty dependency array - only run once on mount

  const initializeExam = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch CBT data
      const cbtResponse = await axios.get(VerifyPublicGetCbtPassCode + cbt_pass_code, config);
      const duration = cbtResponse.data.data.cbt_duration;
      setTimeLeft(duration);
      // toast.success(cbtResponse.data.message);
      if (cbtResponse.data.data) set_cbt_data(cbtResponse.data.data);

      // Fetch questions - ONLY ONCE during initialization
      const questionResponse = await axios.get(GetQuestion, config);
      // toast.success(questionResponse.data.message);
      if (questionResponse.data.data) {
        set_cbt_test_id(questionResponse.data.permit.cbt_test_id);
        set_db_cbt_question(questionResponse.data.data);
        setsubmittedGrade(questionResponse.data.final_grade_submitted);
        setExamStarted(true);
      }
    } catch (err) {
      console.error('Error initializing exam:', err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to load exam";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Timer effect
  useEffect(() => {
    if (timeLeft <= 0 || !cbt_test_id || submittedGrade || !examStarted) return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timeLeft, cbt_test_id, submittedGrade, examStarted]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeDisplayColor = () => {
    if (timeLeft > 600) return 'text-green-600';
    if (timeLeft > 300) return 'text-green-500';
    if (timeLeft > 180) return 'text-yellow-600';
    if (timeLeft > 60) return 'text-orange-600';
    return 'text-red-600 animate-pulse font-bold';
  };

  const getTimeWarningLevel = () => {
    if (timeLeft > 300) return 'normal';
    if (timeLeft > 60) return 'warning';
    return 'critical';
  };

  const handleAutoSubmit = async () => {
    if (submitting || submittedGrade) return;
    
    try {
      setSubmitting(true);
      toast.info('Auto-submitting exam due to time limit...');
      await gradeStudent();
      navigate('/participate/test_completion');
    } catch (err) {
      console.error('Error auto-submitting exam:', err);
      toast.error('Failed to auto-submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const gradeStudent = async () => {
    try {
      setSubmitting(true);
      const payload = {
        cbt_pass_code: cbt_pass_code,
        cbt_test_id: cbt_test_id,
      };

      const response = await axios.post(FinalizedGrade, payload, config);
      toast.success(response.data.message || "CBT submitted successfully!");
      setsubmittedGrade(true);
      setOpen(false);
      navigate('/participate/test_completion');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || "Submission failed";
      toast.error(errorMsg);
      console.error("Grade error:", error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFlagQuestion = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
        toast.info('Question unflagged');
      } else {
        newSet.add(questionId);
        toast.info('Question flagged for review');
      }
      return newSet;
    });
  };

  // Update question answer without refreshing the whole page
  const updateQuestionAnswer = (questionId, userOption) => {
    set_db_cbt_question(prev => 
      prev.map(q => 
        q.cbt_question_id === questionId 
          ? { ...q, user_option: userOption }
          : q
      )
    );
  };

  const progress = db_cbt_question.length > 0 ? ((currentQuestionIndex + 1) / db_cbt_question.length) * 100 : 0;
  const answeredCount = db_cbt_question.filter(q => q.user_option).length;
  const flaggedCount = flaggedQuestions.size;

  // Navigation functions - these only update state, no server calls
  const goToNextQuestion = () => {
    if (currentQuestionIndex < db_cbt_question.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToPrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < db_cbt_question.length) {
      setCurrentQuestionIndex(index);
      setShowMobileSidebar(false);
    }
  };

  // Enhanced question navigation with better answered tracking
  const getQuestionStatus = (question) => {
    if (question.user_option) return 'answered';
    if (flaggedQuestions.has(question.cbt_question_id)) return 'flagged';
    return 'unanswered';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered': return 'bg-green-500 text-white';
      case 'flagged': return 'bg-yellow-100 text-yellow-800 border-yellow-400';
      default: return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Your CBT</h3>
          <p className="text-gray-600 mb-4">Please wait while we prepare your questions</p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Unable to Start Exam</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={initializeExam}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center"
            >
              <i className="fas fa-redo mr-2"></i>
              Try Again
            </button>
            <a
              href="https://cbthost.com"
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-center flex items-center justify-center"
            >
              <i className="fas fa-home mr-2"></i>
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (db_cbt_question.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-6xl mb-4">📝</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">No Questions Available</h3>
          <p className="text-gray-600 mb-6">This exam doesn't have any questions yet or you may not have permission to access it.</p>
          <a
            href="https://cbthost.com"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <i className="fas fa-home mr-2"></i>
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  const currentQuestionData = db_cbt_question[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      {/* Enhanced Header */}
      <div className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left Section */}
            <div className="flex items-center justify-between lg:justify-start flex-1">
              <div className="flex items-center space-x-3">
                <div className="bg-indigo-600 text-white p-2 rounded-xl shadow-md">
                  <i className="fas fa-graduation-cap text-lg"></i>
                </div>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 line-clamp-1">
                    {cbt_data.cbt_name || 'CBT Examination'}
                  </h1>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>Q{currentQuestionIndex + 1} of {db_cbt_question.length}</span>
                    <span>•</span>
                    <span className="hidden sm:inline">ID: {cbt_test_id}</span>
                  </div>
                </div>
              </div>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                className="lg:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors shadow-sm"
                aria-label="Toggle navigation"
              >
                <i className={`fas ${showMobileSidebar ? 'fa-times' : 'fa-bars'} text-gray-600`}></i>
              </button>
            </div>
            
            {/* Right Section */}
            <div className="flex flex-col sm:flex-row items-center gap-4 flex-1 lg:flex-none">
              {/* Progress Bar */}
              <div className="hidden sm:block w-48">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-2.5 rounded-full transition-all duration-500 shadow-sm"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Timer */}
              <div className="text-center sm:text-right">
                <div className={`text-2xl lg:text-3xl font-mono font-bold ${getTimeDisplayColor()} transition-colors duration-300`}>
                  {formatTime(timeLeft)}
                </div>
                <div className="text-sm text-gray-500 font-medium">
                  Time remaining
                </div>
                {getTimeWarningLevel() !== 'normal' && (
                  <div className={`text-xs font-medium mt-1 ${
                    getTimeWarningLevel() === 'critical' ? 'text-red-500 animate-bounce' : 'text-orange-500'
                  }`}>
                    {getTimeWarningLevel() === 'critical' ? 'HURRY UP!' : 'Time running out!'}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              {!submittedGrade && (
                <button 
                  onClick={() => setOpen(true)}
                  className="w-full sm:w-auto bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg flex items-center justify-center transition-all hover:shadow-xl transform hover:scale-105 text-base"
                >
                  <i className="fas fa-paper-plane mr-2"></i>
                  Submit Test
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar */}
          <div className={`lg:col-span-1 space-y-6 transition-all duration-300 ${
            showMobileSidebar ? 'block fixed inset-0 z-40 bg-white p-6 overflow-y-auto' : 'hidden lg:block'
          }`}>
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:sticky lg:top-24 border border-gray-100">
              {/* Mobile Header */}
              {showMobileSidebar && (
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900">Exam Navigation</h3>
                  <button
                    onClick={() => setShowMobileSidebar(false)}
                    className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-label="Close navigation"
                  >
                    <i className="fas fa-times text-gray-600"></i>
                  </button>
                </div>
              )}
              
              {/* Question Grid */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-map-marker-alt text-blue-500 mr-2"></i>
                  Questions
                </h4>
                <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-5 gap-2">
                  {db_cbt_question.map((question, index) => {
                    const status = getQuestionStatus(question);
                    const isCurrent = currentQuestionIndex === index;
                    
                    return (
                      <button
                        key={question.cbt_question_id}
                        onClick={() => goToQuestion(index)}
                        className={`relative w-10 h-10 rounded-xl text-sm font-semibold transition-all transform hover:scale-110 shadow-sm border-2
                          ${isCurrent 
                            ? 'bg-blue-600 text-white shadow-lg scale-110 ring-2 ring-blue-300 border-blue-600' 
                            : `${getStatusColor(status)} border-transparent hover:border-blue-300`
                          }`}
                        title={`Question ${index + 1}${status === 'answered' ? ' (Answered)' : ''}${status === 'flagged' ? ' (Flagged)' : ''}`}
                      >
                        {index + 1}
                        {status === 'flagged' && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full ring-2 ring-white"></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="space-y-3 mb-6">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                  <i className="fas fa-chart-bar text-green-500 mr-2"></i>
                  Progress
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-xl p-3 text-center border border-green-200">
                    <div className="text-2xl font-bold text-green-600">{answeredCount}</div>
                    <div className="text-sm text-green-700 font-medium">Answered</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{db_cbt_question.length - answeredCount}</div>
                    <div className="text-sm text-blue-700 font-medium">Remaining</div>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-3 text-center border border-yellow-200">
                    <div className="text-2xl font-bold text-yellow-600">{flaggedCount}</div>
                    <div className="text-sm text-yellow-700 font-medium">Flagged</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200">
                    <div className="text-2xl font-bold text-gray-600">{db_cbt_question.length}</div>
                    <div className="text-sm text-gray-700 font-medium">Total</div>
                  </div>
                </div>
              </div>

              {/* Keyboard Shortcuts */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mb-6">
                <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
                  <i className="fas fa-keyboard mr-2"></i>
                  Keyboard Shortcuts
                </h4>
                <div className="space-y-2 text-sm text-blue-700">
                  <div className="flex justify-between items-center">
                    <kbd className="px-2 py-1 bg-white rounded border border-blue-300 text-xs text-blue-700">Ctrl</kbd>
                    <span>+</span>
                    <kbd className="px-2 py-1 bg-white rounded border border-blue-300 text-xs text-blue-700">1-9</kbd>
                    <span className="flex-1 ml-2">Jump to question</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <kbd className="px-2 py-1 bg-white rounded border border-blue-300 text-xs text-blue-700">←</kbd>
                    <span className="flex-1 ml-2">Previous question</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <kbd className="px-2 py-1 bg-white rounded border border-blue-300 text-xs text-blue-700">→</kbd>
                    <span className="flex-1 ml-2">Next question</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {!submittedGrade && (
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setOpen(true);
                      setShowMobileSidebar(false);
                    }}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-3 px-4 rounded-xl transition-all hover:shadow-lg transform hover:scale-105 flex items-center justify-center"
                  >
                    <i className="fas fa-flag-checkered mr-2"></i>
                    Submit Exam
                  </button>
                  <button
                    onClick={() => setShowMobileSidebar(false)}
                    className="w-full bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-xl transition-colors lg:hidden"
                  >
                    Close Menu
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Question Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 border border-gray-100">
              {/* Question Header */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 pb-6 border-b border-gray-200">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Question {currentQuestionIndex + 1}
                    </h3>
                    {currentQuestionData.user_option && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <i className="fas fa-check-circle mr-1"></i>
                        Answered
                      </span>
                    )}
                    {flaggedQuestions.has(currentQuestionData.cbt_question_id) && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        <i className="fas fa-flag mr-1"></i>
                        Flagged
                      </span>
                    )}
                  </div>
                  {/* <p className="text-gray-600 text-lg leading-relaxed">
                    {currentQuestionData?.cbt_question?.replace(/<[^>]*>/g, '').substring(0, 120)}...
                  </p> */}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleFlagQuestion(currentQuestionData.cbt_question_id)}
                    className={`p-3 rounded-xl transition-all transform hover:scale-110 ${
                      flaggedQuestions.has(currentQuestionData.cbt_question_id) 
                        ? 'bg-yellow-100 text-yellow-600 shadow-inner' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 shadow-sm'
                    }`}
                    title={flaggedQuestions.has(currentQuestionData.cbt_question_id) ? 'Remove flag' : 'Flag question'}
                  >
                    <i className={`fas fa-flag${flaggedQuestions.has(currentQuestionData.cbt_question_id) ? '' : '-checkered'} text-lg`}></i>
                  </button>
                  
                  {/* Mobile Navigation */}
                  <div className="flex items-center gap-2 lg:hidden bg-gray-100 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-gray-700">
                      {currentQuestionIndex + 1}/{db_cbt_question.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <Obj
                key={currentQuestionData.cbt_question_id}
                cbt_pass_code={cbt_pass_code}
                cbt_test_id={cbt_test_id}
                qdata={currentQuestionData}
                cbt_question_id={currentQuestionData.cbt_question_id}
                user_option={currentQuestionData.user_option}
                updateQuestionAnswer={updateQuestionAnswer}
                indexof={currentQuestionIndex}
                submittedGrade={submittedGrade}
                onImageClick={handleImageClick}
              />

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 mt-6 border-t border-gray-200">
                <button
                  type='button'
                  onClick={goToPrevQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 text-white rounded-xl transition-all transform hover:scale-105 disabled:transform-none font-medium flex items-center justify-center shadow-md"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Previous Question
                </button>
                
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span className="hidden sm:inline">Navigation:</span>
                  <div className="flex space-x-1">
                    <button
                      type='button'
                      onClick={goToPrevQuestion}
                      disabled={currentQuestionIndex === 0}
                      className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center"
                    >
                      ←
                    </button>
                    <button
                      type='button'
                      onClick={goToNextQuestion}
                      disabled={currentQuestionIndex === db_cbt_question.length - 1}
                      className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center"
                    >
                      →
                    </button>
                  </div>
                </div>
                
                <button
                  type='button'
                  onClick={goToNextQuestion}
                  disabled={currentQuestionIndex === db_cbt_question.length - 1}
                  className="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl transition-all transform hover:scale-105 disabled:transform-none font-medium flex items-center justify-center shadow-md"
                >
                  Next Question
                  <i className="fas fa-arrow-right ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      <Modal
        show={open}
        onHide={() => !submitting && setOpen(false)}
        centered
        backdrop="static"
        keyboard={!submitting}
      >
        <Modal.Header closeButton={!submitting} className="border-b border-gray-200">
          <Modal.Title className="text-xl font-bold text-gray-900 flex items-center">
            <i className="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
            Submit CBT
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-center text-yellow-800 mb-2">
              <i className="fas fa-info-circle mr-2"></i>
              <span className="font-semibold">Important Notice</span>
            </div>
            <p className="text-yellow-700 text-sm">
              You have answered <strong>{answeredCount} out of {db_cbt_question.length}</strong> questions.
              {answeredCount < db_cbt_question.length && (
                <span> <strong>{db_cbt_question.length - answeredCount} questions</strong> remain unanswered.</span>
              )}
            </p>
          </div>
          
          <p className="text-gray-700 text-center mb-6 leading-relaxed">
            Are you sure you want to submit your CBT? This action cannot be undone and you will not be able to change your answers after submission.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => setOpen(false)}
              disabled={submitting}
              className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white py-3 px-6 rounded-xl transition-colors font-medium flex items-center justify-center"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Continue CBT
            </button>
            <button
              onClick={gradeStudent}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white py-3 px-6 rounded-xl transition-colors font-medium flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  Submit CBT
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Image Modal */}
      {expandedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="fas fa-image text-blue-500 mr-2"></i>
                Image Preview
              </h3>
              <button 
                onClick={() => setExpandedImage(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <div className="p-4 flex items-center justify-center bg-gray-50 max-h-[70vh] overflow-auto">
              <img 
                src={expandedImage} 
                alt="Expanded diagram" 
                className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
              />
            </div>
            
            <div className="p-4 border-t border-gray-200 bg-white text-center">
              <button 
                onClick={() => setExpandedImage(null)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Floating Action Button */}
      {!showMobileSidebar && (
        <div className="lg:hidden fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowMobileSidebar(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-2xl transition-all transform hover:scale-110 animate-bounce"
            aria-label="Open navigation"
          >
            <i className="fas fa-bars text-xl"></i>
          </button>
        </div>
      )}

      {/* Time Warning Banner */}
      {timeLeft > 0 && timeLeft <= 300 && !submittedGrade && (
        <div className="fixed bottom-4 left-4 right-4 z-30">
          <div className={`max-w-4xl mx-auto p-4 rounded-xl shadow-2xl ${
            getTimeWarningLevel() === 'critical' 
              ? 'bg-gradient-to-r from-red-600 to-red-700 animate-pulse' 
              : 'bg-gradient-to-r from-orange-500 to-orange-600'
          } text-white`}>
            <div className="flex items-center justify-between flex-col sm:flex-row gap-3">
              <div className="flex items-center flex-1">
                <span className="text-2xl mr-3">⏰</span>
                <div>
                  <p className="font-bold text-lg">
                    {timeLeft <= 60 
                      ? 'LESS THAN 1 MINUTE REMAINING!' 
                      : 'TIME IS RUNNING OUT!'
                    }
                  </p>
                  <p className="text-sm opacity-90">
                    {timeLeft <= 60 
                      ? 'Submit your exam immediately to avoid automatic submission!' 
                      : 'Please review your answers and submit when ready.'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(true)}
                className="px-6 py-2 bg-white text-red-600 rounded-lg hover:bg-gray-100 font-bold transition-colors whitespace-nowrap shadow-lg transform hover:scale-105"
              >
                SUBMIT NOW
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer 
        position="bottom-right" 
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

// Fixed Obj component with proper option selection and image handling
const Obj = forwardRef(({ 
  cbt_pass_code, 
  cbt_test_id, 
  qdata, 
  cbt_question_id, 
  user_option, 
  updateQuestionAnswer, 
  indexof, 
  submittedGrade,
  onImageClick 
}, ref) => {
  // Use the user_option from props as the source of truth
  const currentOptionId = user_option ? user_option.cbt_option_id : "";
  const [click_state, set_click_state] = useState(false);
  const [theory, set_theory] = useState(user_option ? user_option.cbt_option_id : '');
  const config = ajaxconfig();

  // Handle option change - update parent state immediately
  const handleOptionChange = async (e) => {
    const newOptionId = e.target.value;
    
    if (!newOptionId || click_state || submittedGrade) return;
    
    // Update parent state immediately for better UX
    updateQuestionAnswer(cbt_question_id, { cbt_option_id: newOptionId });
    
    set_click_state(true);
    
    const data = {
      cbt_question_id: cbt_question_id,
      cbt_option_id: newOptionId,
      cbt_pass_code: cbt_pass_code,
      cbt_test_id: cbt_test_id,
      option_count: qdata.cbt_answer?.length || 0,
      theory_status: false,
    };

    try {
      const response = await axios.post(StudentAnswerSubmit, data, config);
      toast.success(response.data.message);
      // Parent state is already updated, no need to update again
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to submit answer");
      // Revert the change if submission failed
      updateQuestionAnswer(cbt_question_id, null);
    } finally {
      set_click_state(false);
    }
  };

  // Handle theory answer change
  const handleTheoryChange = (content) => {
    set_theory(content);
  };

  // Submit theory answer
  const theory_submit = async (e) => {
    e.preventDefault();
    if (!theory.trim() || click_state || submittedGrade) return;
    
    set_click_state(true);
    
    const data = {
      cbt_question_id: cbt_question_id,
      cbt_option_id: theory,
      cbt_pass_code: cbt_pass_code,
      cbt_test_id: cbt_test_id,
      option_count: qdata.cbt_answer?.length || 0,
      theory_status: true,
    };

    try {
      const response = await axios.post(StudentTheorySubmit, data, config);
      toast.success(response.data.message);
      updateQuestionAnswer(cbt_question_id, { cbt_option_id: theory });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to submit answer");
    } finally {
      set_click_state(false);
    }
  };

  // Clear answer
  const clearAnswer = async (e) => {
    e.preventDefault();
    if (click_state || submittedGrade) return;
    
    set_click_state(true);
    
    const data = {
      cbt_question_id: cbt_question_id,
      cbt_pass_code: cbt_pass_code,
      cbt_test_id: cbt_test_id,
    };

    try {
      const response = await axios.post(StudentClearAnswer, data, config);
      toast.success(response.data.message);
      set_theory("");
      updateQuestionAnswer(cbt_question_id, null);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to clear answer");
    } finally {
      set_click_state(false);
    }
  };

  // Handle image clicks in rich text
  const handleRichTextClick = (e) => {
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      e.stopPropagation();
      onImageClick(e.target.src);
    }
  };

  // Fixed HTML content with proper image handling - handles any img tag format
  const getSafeHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Handle both self-closing and regular img tags
    return htmlContent
      .replace(
        /<img\s+([^>]+?)\s*\/?>/g,
        '<img $1 style="cursor: pointer; max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0;" class="rich-text-image hover:opacity-90 transition-opacity" />'
      )
      .replace(
        /<img\s+([^>]*?)>/g,
        '<img $1 style="cursor: pointer; max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0;" class="rich-text-image hover:opacity-90 transition-opacity" />'
      );
  };

  // Fixed HTML content for options
  const getSafeOptionHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    return htmlContent
      .replace(
        /<img\s+([^>]+?)\s*\/?>/g,
        '<img $1 style="cursor: pointer; max-width: 100%; height: auto; border-radius: 8px; margin: 4px 0;" class="rich-text-image hover:opacity-90 transition-opacity" />'
      )
      .replace(
        /<img\s+([^>]*?)>/g,
        '<img $1 style="cursor: pointer; max-width: 100%; height: auto; border-radius: 8px; margin: 4px 0;" class="rich-text-image hover:opacity-90 transition-opacity" />'
      );
  };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm" id={`Question_${indexof + 1}`}>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
          <span className="flex items-center">
            <i className="fas fa-question-circle text-blue-500 mr-2"></i>
            Question {indexof + 1}
          </span>
          <span className="text-sm text-gray-500 font-normal">
            {qdata.cbt_answer?.length > 0 ? 'Multiple Choice' : 'Theory Question'}
          </span>
        </h3>
      </div>
      
      <div className="p-6">
        {/* Question Text with image click support */}
        <div 
          className="prose prose-lg max-w-none mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200 rich-text-content"
          onClick={handleRichTextClick}
        >
          <div 
            className="text-gray-800 leading-relaxed text-base sm:text-lg"
            dangerouslySetInnerHTML={{ 
              __html: getSafeHtmlContent(qdata.cbt_question)
            }} 
          />
        </div>

        {/* Options */}
        <div className="mb-6">
          {qdata.cbt_answer?.length > 0 ? (
            <div className="space-y-3">
              {qdata.cbt_answer.map((option_data) => (
                <label 
                  key={option_data.answer_id} 
                  className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    currentOptionId === option_data.answer_id 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  } ${submittedGrade ? 'opacity-80' : ''}`}
                >
                  <input
                    type="radio"
                    name={`question_${cbt_question_id}`}
                    value={option_data.answer_id}
                    checked={currentOptionId === option_data.answer_id}
                    onChange={handleOptionChange}
                    className="mt-1 form-radio h-5 w-5 text-blue-600 border-2 border-gray-300"
                    disabled={submittedGrade || click_state}
                  />
                  <div 
                    className="ml-4 prose prose-blue max-w-none flex-1 text-gray-700 text-base"
                    dangerouslySetInnerHTML={{ 
                      __html: getSafeOptionHtmlContent(option_data.options)
                    }} 
                    onClick={handleRichTextClick}
                  />
                </label>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <label className="block text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="fas fa-edit text-blue-500 mr-2"></i>
                Your Answer:
              </label>
              <div className="border border-gray-300 rounded-xl overflow-hidden">
                <MyTextEditor
                  initialContent={theory}
                  onChange={handleTheoryChange}
                  imageLibrary={[]}
                  disabled={submittedGrade || click_state}
                  placeholder="Type your detailed answer here..."
                  height="300px"
                />
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {!submittedGrade && (
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            {qdata.cbt_answer?.length > 0 ? (
              <>
                <button 
                  className="px-6 py-3 border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-xl flex items-center justify-center transition-colors disabled:opacity-50 font-medium"
                  onClick={clearAnswer}
                  disabled={click_state || !currentOptionId}
                >
                  <i className="fas fa-trash-alt mr-2"></i>
                  Clear Answer
                </button>
              </>
            ) : (
              <>
                <button 
                  type='button'
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center justify-center transition-all transform hover:scale-105 shadow-md disabled:opacity-50 disabled:transform-none font-medium"
                  onClick={theory_submit}
                  disabled={click_state || !theory.trim()}
                >
                  {click_state ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle mr-2"></i>
                      Submit Answer
                    </>
                  )}
                </button>
                <button 
                  type='button'
                  className="px-6 py-3 border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-xl flex items-center justify-center transition-colors disabled:opacity-50 font-medium"
                  onClick={clearAnswer}
                  disabled={click_state || !theory.trim()}
                >
                  <i className="fas fa-trash-alt mr-2"></i>
                  Clear Answer
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// Alias component for consistency
const Optionselected = forwardRef((props, ref) => {
  return <Obj {...props} />;
});

export default CbtQuestion;